<script setup>
import { computed } from 'vue';
import { formatSignedPercent, signClass } from '../format';
import { t } from '../i18n';
import { sectors } from '../marketOverview';
import { PANEL } from '../ui';

// «Секторы за день» (docs/market-overview/SPEC.md): the change of each sector with index weights,
// best first, a bar right or left of zero.
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const rows = computed(() => {
  const list = sectors(props.items);
  const max = Math.max(...list.map((s) => Math.abs(s.change)), 0);
  return list.map((sector) => {
    const shown = Number(sector.change.toFixed(2));
    return {
      key: sector.key,
      name: t(`sector.${sector.key}`),
      change: formatSignedPercent(shown, 2),
      changeClass: signClass(shown),
      // Half of the track is the biggest move of the day.
      width: max > 0 ? (Math.abs(sector.change) / max) * 50 : 0,
      up: sector.change >= 0,
    };
  });
});
</script>

<template>
  <section
    :class="PANEL"
    :aria-busy="loading ? 'true' : undefined"
  >
    <h2 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
      {{ t('market.sectors.title') }}
    </h2>
    <p class="mt-0.5 text-[0.8125rem] text-slate-500">
      {{ t('market.sectors.hint') }}
    </p>

    <div
      v-if="loading && items.length === 0"
      class="mt-3 space-y-2"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="h-9 rounded bg-slate-800 motion-safe:animate-pulse"
      />
    </div>
    <p
      v-else-if="rows.length === 0"
      class="mt-4 text-[0.9375rem] text-slate-400"
    >
      {{ t('market.noData') }}
    </p>
    <ul
      v-else
      class="mt-3 space-y-2.5"
    >
      <li
        v-for="row in rows"
        :key="row.key"
      >
        <div class="flex items-baseline justify-between gap-3 text-[0.9375rem]">
          <span class="min-w-0 truncate text-slate-100">{{ row.name }}</span>
          <span
            class="shrink-0 tabular-nums"
            :class="row.changeClass"
          >{{ row.change }}</span>
        </div>
        <!-- Zero in the middle: growth to the right, a fall to the left -->
        <div
          class="relative mt-1 h-1.5 rounded-full bg-slate-800"
          aria-hidden="true"
        >
          <span class="absolute inset-y-0 left-1/2 w-px bg-slate-600" />
          <span
            class="absolute inset-y-0 rounded-full"
            :class="row.up ? 'left-1/2 bg-emerald-500' : 'right-1/2 bg-rose-500'"
            :style="{ width: `${row.width}%` }"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
